<!--
  - Copyright (c) 2021. Trade Research Advisory (Pty) Ltd, P.O.Box 336, Cornwall Hill, Irene, 0178
  - #  This file forms part of the Trade-DSM application frontend.
  - #  https://github.com/Tooblippe/trade-dsm-frontend
  -->

<template>
  <v-navigation-drawer
    v-model="drawerOpen"
    app
    class="grey lighten-4 pl-2 pr-2 font-weight-light"
    clipped
    fixed
    stateless="stateless"
    temporary
    width="1050
"
  >
    <div class="outline ma-1">
      <v-layout class="ma-1 justify-space-between pt-2 pa-1 no-gutters" row>
        <v-flex class="sm5 justify-center">
          <v-switch
            v-model="advanced"
            :label="`${advanced ? 'Disable' : 'Enable'} advanced filters.`"
            Flat
            class="ma-0 pa-0"
            color="success"
            dense
            @change="repaint"
          ></v-switch>
        </v-flex>

        <template v-if="!filters_updating && filters" class="justify-end">
          <v-flex>
            <v-tooltip left color="success" max-width="200px">
              <template v-slot:activator="{on, attrs}">
                <span v-on="on">
                  <v-btn
                    :class="{
                      'v-btn--outlined': true,
                      'trade-blue': true,
                      grey: $store.state.tradeDSM.updatingMainFilters,
                      'white--text': false,
                    }"
                    :disabled="
                      $store.state.tradeDSM.updatingMainFilters || !filters_changed
                    "
                    class="v-btn--outlined yellow black--text"
                    elevation="0"
                    small
                    outlined
                    @click="update_filters_state"
                  >
                    <v-icon left>mdi-exit-to-app</v-icon>
                    Apply
                  </v-btn>
                </span>
              </template>
              <span>{{ apply_text }}</span>
            </v-tooltip>
          </v-flex>

          <v-flex>
            <v-tooltip bottom color="success">
              <template v-slot:activator="{on, attrs}">
                <v-btn
                  :class="{
                    'v-btn--outlined': true,
                    'trade-blue': true,
                    grey: $store.state.tradeDSM.updatingMainFilters,
                    'white--text': true,
                  }"
                  :disabled="$store.state.tradeDSM.updatingMainFilters"
                  class="v-btn--outlined"
                  elevation="0"
                  small
                  x-small
                  v-on="on"
                  @click="clear_presets_from_filterbox"
                >
                  <v-icon left>mdi-delete</v-icon>
                  Clear
                </v-btn>
              </template>
              <span>Clear preset filters (Exclusions Unchanged)</span>
            </v-tooltip>
          </v-flex>

          <v-flex>
            <v-tooltip bottom color="success">
              <template v-slot:activator="{on, attrs}">
                <v-btn
                  class="v-btn--outlined trade-blue white--text"
                  elevation="0"
                  small
                  v-on="on"
                  :disabled="$store.state.tradeDSM.updatingMainFilters"
                  :class="{
                    'v-btn--outlined': true,
                    'trade-blue': true,
                    grey: $store.state.tradeDSM.updatingMainFilters,
                    'white--text': true,
                  }"
                  @click="load_presets_from_filterbox"
                >
                  <v-icon left>mdi-sync</v-icon>
                  Load Preset Filters
                </v-btn>
              </template>
              <span>Load Presets Filters (Exclusions Unchanged) </span>
            </v-tooltip>
          </v-flex>

          <v-flex>
            <v-tooltip bottom color="success" max-width="200px">
              <template v-slot:activator="{on, attrs}">
                <v-btn
                  :class="{
                    'v-btn--outlined': true,
                    'trade-blue': true,
                    grey: $store.state.tradeDSM.updatingMainFilters,
                    'white--text': true,
                  }"
                  :disabled="$store.state.tradeDSM.updatingMainFilters"
                  elevation="0"
                  small
                  x-small
                  v-on="on"
                  @click="cancel_transaction"
                >
                  <v-icon left>mdi-cancel</v-icon>
                  {{ filters_changed ? 'Cancel' : 'Close' }}
                </v-btn>
              </template>
              <span>{{ cancel_text }}</span>
            </v-tooltip>
          </v-flex>
        </template>
        <template v-else>
          <div>
            <span class="blink">Please wait while filters update ...</span>
            <v-progress-circular
              v-if="filters_updating"
              :width="2"
              class="mr-10"
              color="green"
              indeterminate
            ></v-progress-circular>
          </div>
        </template>
      </v-layout>

      <v-layout class="text-left ml-0 mt-4 mr-0" row>
        <v-flex class="sm-9">
          <h3 class="text-left trade-blue-text ml-4">DSM filters and sub-filters:</h3>
        </v-flex>

        <v-flex class="sm-3 ml-12 text-right mr-5 mb-1">
          <temp-dsm-combintations
            :combinations="filters.META.combinations"
            :updating="$store.state.tradeDSM.updatingMainFilters"
            :active="update_combos"
          />
        </v-flex>
      </v-layout>
    </div>

    <v-layout class="text-left ml-0 mt-4 mr-0" row>
      <v-expansion-panels
        :key="force_update"
        v-model="openedPanel"
        accordion
        class="ma-0 pa-2 mr-1 ml-1"
        dense
        focusable
        multiple
      >

        <filter_group
          :advanced="advanced"
          :filter_data="filters.F1"
          class="ma-0"
          filter_name="F1"
          :filters_updating_status="$store.state.tradeDSM.updatingMainFilters"
          :filters_changed="filters_changed"
        />
        <filter_group
          :advanced="advanced"
          :filter_data="filters.F2"
          filter_name="F2"
          :filters_updating_status="$store.state.tradeDSM.updatingMainFilters"
          :filters_changed="filters_changed"
        />
        <filter_group
          :advanced="advanced"
          :filter_data="filters.F3"
          filter_name="F3"
          :filters_updating_status="$store.state.tradeDSM.updatingMainFilters"
          :filters_changed="filters_changed"
        />
        <filter_group
          :advanced="advanced"
          :filter_data="filters.F4"
          filter_name="F4"
          :filters_updating_status="$store.state.tradeDSM.updatingMainFilters"
          :filters_changed="filters_changed"
        />

        <provinvial_filter
          class="mr-1"
          v-if="$store.state.app.selectedModelName !== 'National'"
        />

        <reo-filters class="mr-2"></reo-filters>

      </v-expansion-panels>
    </v-layout>

    <!--      <div v-else>loading....{{ get_filters_loading_status }}</div>-->
  </v-navigation-drawer>
</template>

<script>
import filter_group from '@/components/trade-dsm-application/left-side-bar/filter_componenets/filter_group';
import provinvial_filter from '@/components/trade-dsm-application/left-side-bar/filter_componenets/provinvial_filter';
import {mapGetters, mapMutations} from 'vuex';
import TempDsmCombintations from '@/components/trade-dsm-application/left-side-bar/filter_componenets/TempDsmCombintations';
import ReoFilters from '@/components/trade-dsm-application/left-side-bar/filter_componenets/reo-filters';
import {eventBus} from '@/main';

export default {
  name: 'mainFilters',
  components: {
    ReoFilters,
    TempDsmCombintations,
    filter_group,
    provinvial_filter,
  },
  props: {
    visible: Boolean,
    filters: Object,
  },
  mounted: async function() {
    // await this.load_preset();
    this.force_update += 1;

    eventBus.$on('FILTER_CHANGED', () => {
      this.filters_changed = true;
    });
  },

  watch: {
    advanced: function() {
      if (this.advanced) {
        this.openedPanel = [0];
      } else {
        this.openedPanel = [];
      }
    },
    async drawerOpen(newv) {
      // clean the circular ref

      if (newv === true) {
        // only run on open
        await this.update_filters_selection(true);
      }

      this.clear_circ_ref_error();
      this.searchQuery = '';
      this.skip_cancel = true;

      if (newv) {
        await this.$removeItem('filters-open-backup');
        await this.$setItem(
          'filters-open-backup',
          this.$store.state.tradeDSM.dsm_filter_setttings.Filters,
        );
      }
      this.$forceUpdate();
      this.force_update += 1;
    },
  },
  data: () => ({
    force_update: 1,
    update_combos: true,
    advanced: false,
    filters_updating: false,
    openedPanel: [],
    filters_changed: false, //was changes made to filter, if not hide app
  }),
  computed: {
    ...mapGetters([
      'get_filters_loading_status',
      'get_advanved_filters_is_dirty',
      'countriesSelectorList',
    ]),

    cancel_text() {
      return this.filters_changed
        ? 'Cancel all current changes which have not been applied using the [APPLY] button and close this window.'
        : 'Close this window.';
    },
    apply_text() {
      return this.filters_changed
        ? 'Click [APPLY] to extract updated results for current filter changes.'
        : 'This button will be active when changes are made to the current filter settings.';
    },
    drawerOpen: {
      get() {
        return this.$store.state.tradeDSM.mainFilterOpen;
      },
      set() {
        this.filters_changed = false;
        // pass
      },
    },
  },

  methods: {
    ...mapMutations([
      'set_dsm_filters_loading',
      'set_dsm_filters',
      'set_dsm_filters_from_localforage',
      'setCombinationsTradeValue',
      'set_filter_box_selection_country',
      'set_filter_box_selection_product',
      'set_advanced_filters_is_dirty',
    ]),
    load_presets_from_filterbox() {
      this.filters_changed = true;
      this.load_preset(null, true);
    },
    clear_presets_from_filterbox() {
      this.filters_changed = true;
      // clear the reo filters
      eventBus.$emit('FILTER_CLEARED_EVENT');
      this.clear_preset_filters(false);
    },
    repaint() {
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    update_state(filter_name, id, value) {
      let value_to_set = {
        filter_name: filter_name,
        id: id,
        value: 0,
        selected_value: value,
      };
      // console.log('updating store', value_to_set);
      this.$store.commit('setFilter', value_to_set);

      // now we need to request an update from the server below this
    },

    async cancel_transaction() {
      this.filters_changed = false;
      this.$store.commit('toggleMainFilter', false);
      await this.set_dsm_filters_from_localforage(
        await this.$getItem('filters-open-backup'),
      );
      await this.$removeItem('filters-open-backup');
    },

    async update_filters_state() {
      this.$store.commit('toggleMainFilter', false);
      // this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.filter_in_progress = true;
      await this.update_results_cube();
    },
  },
};
</script>

<style scoped>
h3 {
  text-align: left;
  padding-top: 10px;
  margin-left: 4px;
}

.blink {
  color: green;
  -webkit-animation: flash linear 1s infinite;
  animation: flash linear 2s infinite;
}
@-webkit-keyframes flash {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.1;
  }
  100% {
    opacity: 1;
  }
}
@keyframes flash {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.1;
  }
  100% {
    opacity: 1;
  }
}
/* Pulse class and keyframe animation */
.pulseit {
  -webkit-animation: pulse linear 0.5s infinite;
  animation: pulse linear 0.5s infinite;
}
@-webkit-keyframes pulse {
  0% {
    width: 200px;
  }
  50% {
    width: 340px;
  }
  100% {
    width: 200px;
  }
}
@keyframes pulse {
  0% {
    width: 200px;
  }
  50% {
    width: 340px;
  }
  100% {
    width: 200px;
  }
}
</style>
